import { z } from "zod";

/** Resolutions and spot checks, the pure half (M5 round nine, task 6's second half; choices 4 and 19; DECISIONS 2026-09-17): the
 *  resolution window, the spot-check sampling rule, the verdict's body and the two notes. Nothing here touches a database. */
/** a reply counts as a resolution when this many days pass without a customer message after it (choice 4) */
export const RESOLUTION_WINDOW_DAYS = 7;
/** every automatic reply is spot-checked for this long after the workspace's first automatic send (choice 19) */
export const SPOT_CHECK_ALL_DAYS = 21;
/** then a seeded sample of this share, weighted toward confidence within this distance of the threshold */
export const SPOT_CHECK_RATE = 0.15;
export const SPOT_CHECK_NEAR_THRESHOLD = 0.03;
/** the customer's answer within this many days of an automatic reply puts it on the spot-check list */
export const SPOT_CHECK_ANSWERED_DAYS = 2;
export const SPOT_CHECKS = ["due", "good", "wrong"] as const;
export type SpotCheckValue = (typeof SPOT_CHECKS)[number];
export const SPOT_CHECK_WRONG_NOTE = "Spot check: the automatic reply was marked wrong. Needs a human answer.";
export const SPOT_CHECK_UNDONE_NOTE = "Spot check undone";
export const SpotCheckVerdictSchema = z.object({ verdict: z.enum(["good", "wrong"]).nullable() }).strict();

const DAY = 86_400_000;
/** a deterministic draw in [0, 1) from the seed (the draft's id): the same reply is picked or left out whenever the rule is asked.
 *  FNV-1a over the string with MurmurHash3's finaliser (the browser shares this module, so no node:crypto) */
function draw(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 0x1_0000_0000;
}

/** whether an automatic reply is put on the spot-check list: every reply within the first weeks of the workspace's first automatic
 *  send; after that always when the confidence sits within 0.03 of the threshold (as the float4 column stores them), else a seeded
 *  15 % sample */
export function spotCheckDue(i: { firstAutoSendAt: Date | null; now: Date; confidence: number; threshold: number; seed: string }): boolean {
  if (!i.firstAutoSendAt || i.now.getTime() - i.firstAutoSendAt.getTime() < SPOT_CHECK_ALL_DAYS * DAY) return true;
  if (Math.abs(Math.fround(i.confidence) - Math.fround(i.threshold)) <= SPOT_CHECK_NEAR_THRESHOLD + 1e-7) return true;
  return draw(i.seed) < SPOT_CHECK_RATE;
}
/** whether a reply's resolution is due: the window has passed and no customer message came after the reply */
export function resolutionDue(i: { sentAt: Date; lastCustomerAt: Date | null; now: Date }): boolean {
  if (i.now.getTime() - i.sentAt.getTime() < RESOLUTION_WINDOW_DAYS * DAY) return false;
  return i.lastCustomerAt === null || i.lastCustomerAt.getTime() <= i.sentAt.getTime();
}
